import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// NOT: /api/customer-tasks list endpoint'i OData değil (özel REST: ?status=&take=).
// Bu yüzden useC360List (OData $top/$skip/$filter) yerine REST fetch + istemci tarafı
// filtre/sıralama/sayfalama kullanılır. Görsel/yerleşim tamamen c360 deseni.
// Kayıt ekleme/görüntüleme/düzenleme artık tam-sayfa detaya (/customer-tasks/:id) gider — bu ekran liste-only.
const API_BASE = '/api/customer-tasks';
const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Açık' },
  { value: 'DONE', label: 'Tamamlandı' },
  { value: 'CANCELLED', label: 'İptal' },
];
const statusLabel = (v) => {
  const f = STATUS_OPTIONS.find((o) => o.value === v);
  return f ? f.label : (v || '-');
};
const statusTone = (v) => (v === 'DONE' ? 'ok' : v === 'CANCELLED' ? 'neutral' : 'wait');

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    openCount: pick(data, 'openCount', 'OpenCount') ?? 0,
    doneCount: pick(data, 'doneCount', 'DoneCount') ?? 0,
    cancelledCount: pick(data, 'cancelledCount', 'CancelledCount') ?? 0,
  };
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

const COLUMN_FILTER_META = {
  title: { type: 'text' },
  assigneeName: { type: 'text' },
  customerTitle: { type: 'text' },
  assignedByName: { type: 'text' },
  status: { type: 'select', options: STATUS_OPTIONS },
};

export function CustomerTasksPage() {
  usePageMeta('Saha Görevleri', 'Ofisten saha temsilcisine atanan görevler');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [allRows, setAllRows] = useState([]);
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=500`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setAllRows(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // === İstemci tarafı filtre + sıralama + sayfalama ===
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const colMatch = (row) => {
      for (const [field, filter] of Object.entries(columnFilters)) {
        if (!filter) continue;
        const meta = COLUMN_FILTER_META[field];
        if (!meta) continue;
        const raw = pick(row, field, field.charAt(0).toUpperCase() + field.slice(1));
        if (meta.type === 'select') {
          if (filter.value === '' || filter.value == null) continue;
          if (String(raw ?? '') !== String(filter.value)) return false;
        } else {
          const fv = (filter.value ?? '').toString().toLocaleLowerCase('tr-TR');
          if (fv && !String(raw ?? '').toLocaleLowerCase('tr-TR').includes(fv)) return false;
        }
      }
      return true;
    };
    const searchMatch = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'title', 'Title'),
        pick(row, 'description', 'Description'),
        pick(row, 'assigneeName', 'AssigneeName'),
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'assignedByName', 'AssignedByName'),
      ];
      return fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = allRows.filter((r) => searchMatch(r) && colMatch(r));
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'AssigneeName': return pick(row, 'assigneeName', 'AssigneeName') || '';
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'AssignedByName': return pick(row, 'assignedByName', 'AssignedByName') || '';
        case 'Status': return pick(row, 'status', 'Status') || '';
        case 'CompletedAt': {
          const v = pick(row, 'completedAt', 'CompletedAt');
          return v ? new Date(v).getTime() : 0;
        }
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const rows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const counts = useMemo(() => {
    let openCount = 0; let doneCount = 0; let cancelledCount = 0;
    allRows.forEach((r) => {
      const st = pick(r, 'status', 'Status');
      if (st === 'OPEN') openCount += 1;
      else if (st === 'DONE') doneCount += 1;
      else if (st === 'CANCELLED') cancelledCount += 1;
    });
    return { openCount, doneCount, cancelledCount };
  }, [allRows]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  }, []);
  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  }, []);
  const applySort = useCallback((field, dir) => {
    setCurrentPage(1);
    setSortField(field || 'UpdatedDate');
    setSortDir(dir || 'asc');
  }, []);

  // === Navigasyon (tam-sayfa detay) ===
  const openNew = () => navigate('/customer-tasks/new');
  const openDetail = (row) => { if (!row) return; navigate(`/customer-tasks/${pick(row, 'id', 'Id')}`, { state: { row } }); };
  const openEditRow = (row) => { if (!row) return; navigate(`/customer-tasks/${pick(row, 'id', 'Id')}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    const id = pick(selected, 'id', 'Id');
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Görev silindi.');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('title', {
      header: 'Görev', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const t = pick(row.original, 'title', 'Title');
        const d = pick(row.original, 'description', 'Description');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 700 }}>{t || '-'}</span>
            {d && (
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                {String(d).slice(0, 80)}{String(d).length > 80 ? '…' : ''}
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('assigneeName', {
      header: 'Atanan', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('customerTitle', {
      header: 'Müşteri', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '(Genel)',
    }),
    columnHelper.accessor('assignedByName', {
      header: 'Veren', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return <StatusBadge tone={statusTone(v)}>{statusLabel(v)}</StatusBadge>; },
    }),
    columnHelper.accessor('completedAt', {
      header: 'Tamamlanma', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'title', headerName: 'Görev' },
        { field: 'description', headerName: 'Açıklama' },
        { field: 'assigneeName', headerName: 'Atanan' },
        { headerName: 'Müşteri', valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '(Genel)' },
        { field: 'assignedByName', headerName: 'Veren' },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(pick(row, 'status', 'Status')) },
        { headerName: 'Tamamlanma', valueGetter: ({ row }) => formatDate(pick(row, 'completedAt', 'CompletedAt')) },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `saha-gorevleri_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Görev / atanan / müşteri / veren..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Saha Görevleri"
        subtitle="Ofisten saha temsilcisine atanan görevler. Müşteri opsiyoneldir (genel görev de olabilir)."
        meta={(
          <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
            <StatusBadge tone="wait">Açık: {counts.openCount}</StatusBadge>
            <StatusBadge tone="ok">Tamamlanan: {counts.doneCount}</StatusBadge>
            <StatusBadge tone="neutral">İptal: {counts.cancelledCount}</StatusBadge>
          </span>
        )}
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Görev</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{pick(selected, 'title', 'Title')}"</strong> görevini silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CustomerTasksPage;
